// Edit / Delete for a hand-logged or check-off activity on a timeline (Brett, 2026-10-23: wrong
// clicks and test runs). Outlook emails don't get these: they mirror the mailbox. Edits and
// deletes are recorded in activity_audit by the database.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { OUTCOMES, type Outcome } from '../lib/activity';
import { supabase, unwrap } from '../lib/supabase';
import type { TimelineItem } from './Timeline';
import { useToast } from './Toast';
import { Button, cx, ErrorBox, Field, Input, Modal, Select, Textarea } from './ui';

const TYPES = ['call', 'meeting', 'note', 'text', 'email'] as const;

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export function ActivityActions({ item }: { item: TimelineItem }) {
  const [mode, setMode] = useState<'edit' | 'delete' | null>(null);
  if (item.source !== 'manual' && item.source !== 'reminder') return null;
  return (
    <>
      <button className="text-indigo-600 hover:underline" onClick={() => setMode('edit')}>
        edit
      </button>
      <button className="text-rose-600 hover:underline" onClick={() => setMode('delete')}>
        delete
      </button>
      {mode === 'edit' && <EditActivity item={item} onClose={() => setMode(null)} />}
      {mode === 'delete' && <DeleteActivity item={item} onClose={() => setMode(null)} />}
    </>
  );
}

function EditActivity({ item, onClose }: { item: TimelineItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState(item.interaction_type);
  const [direction, setDirection] = useState(item.direction ?? 'out');
  const [outcome, setOutcome] = useState<Outcome | null>((item.outcome as Outcome | null) ?? null);
  const [when, setWhen] = useState(toLocalInput(item.occurred_at));
  const [subject, setSubject] = useState(item.subject ?? '');
  const [body, setBody] = useState(item.body_text ?? '');
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await supabase
          .from('interactions')
          .update({
            interaction_type: type,
            direction: type === 'note' ? null : direction,
            outcome: type === 'call' ? outcome : null,
            occurred_at: new Date(when).toISOString(),
            subject: subject.trim() || null,
            body_text: body.trim() || null,
            snippet: body.trim().slice(0, 200) || null,
          })
          .eq('id', item.id),
      ),
    onSuccess: () => {
      qc.invalidateQueries();
      onClose();
    },
  });
  return (
    <Modal title="Edit activity" open onClose={onClose}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Select value={type} onChange={(e) => setType(e.target.value)} className="!w-32" aria-label="Type">
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t[0]!.toUpperCase() + t.slice(1)}
              </option>
            ))}
          </Select>
          {type !== 'note' && (
            <Select value={direction} onChange={(e) => setDirection(e.target.value)} className="!w-36" aria-label="Direction">
              <option value="out">I reached out</option>
              <option value="in">They reached out</option>
            </Select>
          )}
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="!w-56" aria-label="When" />
        </div>
        {type === 'call' && (
          <div className="flex flex-wrap gap-1.5" aria-label="Call outcome">
            {OUTCOMES.map((o) => (
              <button
                key={o.key}
                onClick={() => setOutcome(outcome === o.key ? null : o.key)}
                className={cx('rounded-full border px-2.5 py-0.5 text-xs', outcome === o.key ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 text-slate-600 hover:border-indigo-300')}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
        <Field label="Subject">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <ErrorBox error={save.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DeleteActivity({ item, onClose }: { item: TimelineItem; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reopen, setReopen] = useState(false);
  const del = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('delete_activity', { p_id: item.id, p_reopen: reopen });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries();
      onClose();
      toast({ message: reopen ? 'Deleted. The reminder is back on your list.' : 'Deleted.', autoHideMs: 5000 });
    },
  });
  return (
    <Modal title="Delete this activity?" open onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-slate-700">
          {item.interaction_type[0]!.toUpperCase() + item.interaction_type.slice(1)}
          {item.subject ? ` — “${item.subject}”` : ''} will be removed from the timeline and won’t count toward your goals.
        </p>
        {item.reminder_id && (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={reopen} onChange={(e) => setReopen(e.target.checked)} />
            Put the reminder it completed back on my to-do list
          </label>
        )}
        <ErrorBox error={del.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" busy={del.isPending} onClick={() => del.mutate()}>
            Delete
          </Button>
        </div>
      </div>
    </Modal>
  );
}
